import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';
import ReactModal from 'react-modal';
import { useTranslation } from 'react-i18next';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import { AxiosError } from 'axios';

import { useUpdateUser, useUpdateUserAndPhone, useUpdateUserSettings } from 'api/user';
import { useUser } from 'hooks/useUser';
import { AddressBookIcon } from 'shared/components/icons/AddressBookIcon';
import { KeyIcon } from 'shared/components/icons/KeyIcon';
import { FileContractIcon } from 'shared/components/icons/FileContractIcon';
import { resolvePath, useLocation, useNavigate } from 'react-router';
import { pages } from 'pages';
import DataDogLogs from 'shared/logging/DataDogLogs';
import { ChangePasswordRequestBody, useChangePassword } from 'api/account';
import { useFeatureFlags } from 'hooks/useFeatureFlags';
import { FlagKey } from 'api/types';
import { getDashboardRedirectPath } from 'modules/auth/Auth.utils';
import { useSearchParams } from 'react-router-dom';
import { useGlobalState } from 'hooks/useGlobalState';
import { AccountIcon } from '../icons/AccountIcon';
import {
  ButtonWrapper,
  InnerContainer,
  modalStyles,
  ContentWrapper,
  AccountMenuHeader,
  SlideWrapper,
  SlideDirection,
} from './AccountMenu.styles';
import { Button } from '../Button/Button';
import { CloseIcon } from '../icons/CloseIcon';
import { AccountBody } from './AccountBody/AccountBody';
import { ContactInfoBody } from './ContactInfoBody/ContactInfoBody';
import { ArrowLeftIcon } from '../icons/ArrowLeftIcon';
import { PhoneInputBody } from './PhoneFormBody/PhoneFormBody';
import { AddressFormBody } from './AddressFormBody/AddressFormBody';
import { PasswordFormBody } from './PasswordFormBody/PasswordFormBody';
import { toast } from '../Toast/Toast';
import { ModalStyleVariant } from '../MessageCenter/types';
import { DeviceIcon } from '../icons/DeviceIcon';
import { SmsOptInBody } from './SmsOptInBody/SmsOptInBody';
import SmsOptInModalBodyType from './SmsOptInBody/SmsOptInModal/types';

export enum ModalBodyType {
  MENU,
  CONTACT_INFORMATION = 'Contact Information',
  PHONE_NUMBER = 'Phone Number',
  ADDRESS = 'Address',
  PASSWORD = 'Password',
  AGREEMENT = 'Agreement',
  SMS_OPT_IN = 'Sms Opt In',
}

type Props = {
  name: string;
  title: string;
  onUnImpersonateUser?: () => void;
  onLogout: () => void;
  modalType_StorybookOnly?: ModalBodyType;
  smsModalType_StorybookOnly?: SmsOptInModalBodyType;
};

/**
 * @param {string} [name] - name of account holder
 * @param {string} [title] - title of the account menu
 * @param {Function} [onUnImpersonateUser] - click handler (optional)
 * @param {Function} [onLogout] - click handler
 */

export function AccountMenu({
  title,
  name,
  onUnImpersonateUser,
  onLogout,
  modalType_StorybookOnly,
  smsModalType_StorybookOnly,
}: Props) {
  const [modalBody, setModalBody] = useState<ModalBodyType>(
    modalType_StorybookOnly || ModalBodyType.MENU,
  );
  const [isClosing, setIsClosing] = useState(false);
  const [slideDirection, setSlideDirection] = useState(SlideDirection.RIGHT);
  const [searchParams] = useSearchParams();
  const {
    flags: { [FlagKey.smsOptIn]: smsFlag, [FlagKey.onboardingRefresh]: onboardingRefresh },
  } = useFeatureFlags();

  const { accountMenuOpen, setAccountMenuOpen } = useGlobalState();

  const { userResult, refetch, selectedAgreement } = useUser();
  const user = userResult?.user;

  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const styles = modalStyles(theme);

  const { hasDashboardAccess } = getDashboardRedirectPath(
    userResult?.user || undefined,
    selectedAgreement,
    onboardingRefresh,
  );

  const onUpdateModalBody = useCallback((modalBodyType: ModalBodyType, goingBack: boolean) => {
    setSlideDirection(goingBack ? SlideDirection.LEFT : SlideDirection.RIGHT);
    setTimeout(() => {
      setModalBody(modalBodyType);
    }); // setTimeout is used to allow slideDirection to update to the correct state before updating the modalBody
  }, []);

  const handleBackClick = useCallback(() => {
    switch (modalBody) {
      case ModalBodyType.PHONE_NUMBER:
        return onUpdateModalBody(ModalBodyType.CONTACT_INFORMATION, true);

      case ModalBodyType.ADDRESS:
        return onUpdateModalBody(ModalBodyType.CONTACT_INFORMATION, true);

      case ModalBodyType.PASSWORD:
        return onUpdateModalBody(ModalBodyType.MENU, true);

      default:
        return onUpdateModalBody(ModalBodyType.MENU, true);
    }
  }, [modalBody, onUpdateModalBody]);

  const changePassword = useChangePassword();

  const changePasswordError = changePassword.error as AxiosError | undefined;

  const handleChangePassword = useCallback(
    async ({ old_password, new_password }: ChangePasswordRequestBody) => {
      try {
        await changePassword.mutateAsync({ old_password, new_password });
        refetch();
        handleBackClick();
        toast({
          type: 'success',
          title: t('toast.success'),
          message: t('account.successfullyUpdated'),
          theme,
        });
      } catch (error) {
        if ((error as AxiosError).response?.status !== 400) {
          toast({
            type: 'error',
            title: t('toast.error'),
            message: t('account.unableToSave'),
            theme,
          });
        }

        DataDogLogs.error(
          'Account - LoginDetailsForm',
          {
            user: user!,
          },
          error as Error | undefined,
        );
      }
    },
    [changePassword, refetch, handleBackClick, t, theme, user],
  );

  useEffect(() => {
    if (isClosing) setIsClosing(false);
  }, [isClosing]);

  useEffect(() => {
    if (searchParams.has('sms_opt_in')) {
      setAccountMenuOpen(true);
      setModalBody(ModalBodyType.SMS_OPT_IN);
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('sms_opt_in');
      navigate(`${location.pathname}?${newParams.toString()}`, { replace: true });
    }
  }, [location.pathname, navigate, searchParams, setAccountMenuOpen]);

  const handleOpenDropdown = () => {
    if (!isClosing) {
      setAccountMenuOpen(true);
    }
    setIsClosing(false);
  };

  const handleUpdateUserSuccess = () => {
    refetch();
    handleBackClick();
    toast({
      type: 'success',
      title: t('toast.success'),
      message: t('account.successfullyUpdated'),
      theme,
    });
  };

  const handleUpdateUserError = () => {
    toast({
      type: 'error',
      title: t('toast.error'),
      message: t('toast.somethingWentWrong'),
      theme,
    });
  };

  const { mutate: updatePhone } = useUpdateUserAndPhone({
    onSuccess: handleUpdateUserSuccess,
    onError: handleUpdateUserError,
  });

  const { mutate: updateUser } = useUpdateUser({
    onSuccess: handleUpdateUserSuccess,
    onError: handleUpdateUserError,
  });

  const { mutate: updateUserSettings } = useUpdateUserSettings({
    onSuccess: (data) => {
      const isOptedIn = data?.sms_opt_in;
      refetch();
      toast({
        type: 'success',
        title: t('toast.success'),
        message: isOptedIn
          ? t('account.smsOptIn.successToastOptIn')
          : t('account.smsOptIn.successToastOptOut'),
        theme,
      });
    },
    onError: () => {
      toast({
        type: 'error',
        title: t('toast.error'),
        message: t('account.smsOptIn.failedToast'),
        theme,
      });
    },
  });
  const handleCloseDropdown = () => {
    setIsClosing(true);
    setAccountMenuOpen(false);
    setModalBody(ModalBodyType.MENU);
  };

  const handleGoToAgreement = useCallback(() => {
    setAccountMenuOpen(false);

    setTimeout(() => {
      navigate(resolvePath(`${pages.AGREEMENTS}/${selectedAgreement?.id}`, `/${pages.DASHBOARD}`));
    }, 300); // timeout needed to wait for animation transition to finish
  }, [selectedAgreement, navigate, setAccountMenuOpen]);

  const accountIcon = () => <AccountIcon filled={false} />;

  const closeIcon = () => <CloseIcon size="large" color={theme.colors.textPrimarySecondary1100} />;

  const arrowLeftIcon = () => (
    <ArrowLeftIcon color={theme.colors.backgroundSystemCtaSecondary2100} height={16} width={16} />
  );
  const accountBodyItems = useMemo(() => {
    const items = [
      {
        title: t('accountMenu.contactInformation'),
        icon: <AddressBookIcon color={theme.colors.backgroundAccentBlueStrongestSecondary1100} />,
        onClick: () => {
          onUpdateModalBody(ModalBodyType.CONTACT_INFORMATION, false);
        },
      },
      {
        title: t('accountMenu.password'),
        icon: <KeyIcon color={theme.colors.backgroundAccentBlueStrongestSecondary1100} />,
        onClick: () => {
          onUpdateModalBody(ModalBodyType.PASSWORD, false);
        },
      },
    ];

    if (hasDashboardAccess) {
      items.push({
        title: t('accountMenu.agreement'),
        icon: <FileContractIcon color={theme.colors.backgroundAccentBlueStrongestSecondary1100} />,
        onClick: handleGoToAgreement,
      });
    }

    if (smsFlag && hasDashboardAccess) {
      items.push({
        title: t('accountMenu.smsOptIn'),
        icon: <DeviceIcon color={theme.colors.backgroundAccentBlueStrongestSecondary1100} />,
        onClick: () => {
          onUpdateModalBody(ModalBodyType.SMS_OPT_IN, false);
        },
      });
    }

    return items;
  }, [
    onUpdateModalBody,
    t,
    theme.colors.backgroundAccentBlueStrongestSecondary1100,
    hasDashboardAccess,
    smsFlag,
    handleGoToAgreement,
  ]);

  const body = useMemo(() => {
    if (!user) {
      return null;
    }

    switch (modalBody) {
      case ModalBodyType.CONTACT_INFORMATION:
        return (
          <ContactInfoBody
            user={user}
            onClickEditPhone={() => {
              setModalBody(ModalBodyType.PHONE_NUMBER);
            }}
            onClickEditAddress={() => {
              setModalBody(ModalBodyType.ADDRESS);
            }}
          />
        );

      case ModalBodyType.PHONE_NUMBER:
        return (
          <PhoneInputBody
            phoneNumber={user.phone.phone_number}
            onSubmit={({ phone_number }) => {
              updatePhone({ user_id: user.id, phoneBody: { phone_number } });
            }}
          />
        );
      case ModalBodyType.ADDRESS:
        return (
          <AddressFormBody
            address={user.address}
            onSubmit={({ address }) => {
              updateUser({ user_id: user.id, body: { address } });
            }}
          />
        );

      case ModalBodyType.PASSWORD:
        return (
          <PasswordFormBody
            email={user.email}
            onSubmit={async ({ oldPassword, newPassword }) => {
              await handleChangePassword({ old_password: oldPassword, new_password: newPassword });
            }}
            is400Error={changePasswordError?.response?.status === 400}
            supportPath={
              hasDashboardAccess
                ? resolvePath(pages.SUPPORT, `/${pages.DASHBOARD}`)
                : resolvePath(`/${pages.SUPPORT}`)
            }
          />
        );
      case ModalBodyType.SMS_OPT_IN:
        return (
          <SmsOptInBody
            user={user}
            updateUserSettings={updateUserSettings}
            smsModalType_StorybookOnly={smsModalType_StorybookOnly}
          />
        );
      default:
        return (
          <AccountBody
            name={name}
            agreementNumber={`${t('accountMenu.agreement')} #${selectedAgreement?.homeowner_contract_id.toUpperCase()}`}
            items={accountBodyItems}
            onUnImpersonateUser={onUnImpersonateUser}
            onLogout={onLogout}
          />
        );
    }
  }, [
    user,
    modalBody,
    changePasswordError?.response?.status,
    hasDashboardAccess,
    updateUserSettings,
    smsModalType_StorybookOnly,
    name,
    t,
    selectedAgreement?.homeowner_contract_id,
    accountBodyItems,
    onUnImpersonateUser,
    onLogout,
    updatePhone,
    updateUser,
    handleChangePassword,
  ]);

  return (
    <>
      <ButtonWrapper>
        <Button
          styleVariant="tertiary"
          Icon={accountIcon}
          label={title}
          onClick={handleOpenDropdown}
        />
      </ButtonWrapper>
      {/* TODO: replace this with the InfoDrawer component (HOP-2887) */}
      <ReactModal
        className="info-drawer__content"
        overlayClassName="info-drawer__overlay"
        contentLabel={`${title} Menu`}
        isOpen={accountMenuOpen}
        preventScroll={false}
        shouldCloseOnOverlayClick
        shouldCloseOnEsc
        onRequestClose={handleCloseDropdown}
        appElement={document.querySelector('#root') as HTMLElement}
        style={styles}
        closeTimeoutMS={300} // matches transition duration defined in GlobalStyles
      >
        <InnerContainer onClick={handleCloseDropdown}>
          <ContentWrapper
            onClick={(event) => {
              event.stopPropagation(); // Prevent triggering InnerContainer's onClick for all child clicks
            }}
          >
            <SwitchTransition mode="out-in">
              <CSSTransition key={modalBody} timeout={300} classNames="slide" unmountOnExit>
                <SlideWrapper $direction={slideDirection}>
                  <AccountMenuHeader
                    $styleVariant={ModalStyleVariant.SECONDARY}
                    $overridePrimaryStyle={modalBody === ModalBodyType.MENU}
                    $feedScrolling={false}
                  >
                    {modalBody !== ModalBodyType.MENU && (
                      <Button
                        styleVariant="tertiary"
                        label={t('cta.back')}
                        Icon={arrowLeftIcon}
                        onClick={handleBackClick}
                        iconCenterAlign
                      />
                    )}
                    <Button
                      styleVariant="tertiary"
                      Icon={closeIcon}
                      onClick={handleCloseDropdown}
                      iconRight
                    />
                  </AccountMenuHeader>
                  {body}
                </SlideWrapper>
              </CSSTransition>
            </SwitchTransition>
          </ContentWrapper>
        </InnerContainer>
      </ReactModal>
    </>
  );
}

export default AccountMenu;
